import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';


/**
 * Add this service to any route's canDeactive property whose component property implements IGenericCanDeactiveComponent.
 * This wll cause component.deactivateCheckHandler.beginLeaving() to be called when the route is being deactivated.
 * @example
 * **On route config**
 * ```
 * routes = [
 *   {
 *     path: 'pathToRoute',
 *     component: SomeComponent,
 *     canDeactive: [GenericCanDeactiveService],
 *   }
 * ];
 * ```
 * **On component**
 * ```
 * export class SomeComponent implements IGenericCanDecativateComponent {
 *   deactivateCheckHandler = new GenericCanDeactiveHandler(() => this.canClose);
 * }
 * ```
 */
@Injectable()
export class GenericCanDeactivateGuard  {
  /**
   * The method called when checking if the route can be deactivated.
   * Calls deactivateCheckHandler.beginLeaving if it exists on the component, otherwises returns true
   */
  canDeactivate(component: GenericCanDeactivateComponent): Observable<boolean>{
    if (!component || !component.deactivationHandler) {
      return of(true);
    }
    component.deactivationHandler.startDeactivation();
    return component.deactivationHandler.stateChange$.pipe(
      filter(x => x === 'active' || x === 'deactivating'),
      map(x => x === 'deactivating')
    );
  }
}
/** any components that use the GenericCanDeactivateGuard should implement this interface */
export interface GenericCanDeactivateComponent {
  /** should return true if this component can be closed immediately, or the result of beginLeaving if awaiting feedback */
  deactivationHandler: GenericCanDeactiveHandler
}

/**
 * will handle all interactions when handling a component deactivation
 */
export class GenericCanDeactiveHandler {
  private stateChangeSubject = new BehaviorSubject<'active' | 'confirming' | 'deactivating'>('active');

  /** The parent component should subscribe to this, and respond by calling either cancelDeactivation or completeDeactivation */
  readonly confirmation$ = this.stateChangeSubject.pipe(filter(x => x === 'confirming'), map(() => undefined));

  /** Used by the GenericCanDeactivateGuard to react to changes. */
  readonly stateChange$ = this.stateChangeSubject.asObservable();

  /** Sets the state to active.  This should cause the GenericCanDeactivateGuard to cancel if called after the gaurd was run. */
  cancelDeactivation() {
    this.stateChangeSubject.next('active');
  }

  /** Sets the state to deactivating.  This should cause the GenericCanDeactivateGuard to close  */
  completeDeactivation() {
    this.stateChangeSubject.next('deactivating');
  }

  /** Sets the state to confirming.  Should only be called by GenericCanDeactivateGuard */
  startDeactivation() {
    this.stateChangeSubject.next('confirming');
  }
}
